// umlaut-check: skip — Datenschluessel ("wochenbedarf_basis", "t_leer" usw.)
// sind technische Bezeichner aus der Prognose-Ausgabe.

import React, {useId} from "react";
import {Card, OverlayTrigger, Tooltip} from "react-bootstrap";
import {PiWarningBold} from "react-icons/pi";
import HeaderWithHelp from "../SalesAnalysis/HeaderWithHelp.jsx";
import SicherheitAbzeichen from "./SicherheitAbzeichen.jsx";
import {formatDatum, formatMenge, herkunftText} from "./format.js";
import {COSMOS_PLANUNG_HINWEIS} from "../Stock/bestandTexte.js";
import {TOOLTIP_POPPER_CONFIG} from "./tooltipConfig.js";

function Kachel({label, hilfe, children, warnung}) {
    return (
        <Card className={`shadow-sm h-100 ${warnung ? "border-danger" : ""}`} style={{minWidth: 190}}>
            <Card.Body className="py-2 px-3">
                <div className="small text-muted mb-1">
                    <HeaderWithHelp help={hilfe}>{label}</HeaderWithHelp>
                </div>
                {children}
            </Card.Body>
        </Card>
    );
}

function WertMitHerkunft({feld, suffix = "", format = formatMenge}) {
    const eindeutigeId = useId();

    if (!feld || feld.wert === null || feld.wert === undefined) {
        return <span className="text-muted">–</span>;
    }
    const text = herkunftText(feld);
    const inhalt = (
        <span className="d-inline-flex align-items-center gap-1">
            <span className="fw-semibold fs-5">
                {format(feld.wert)}
                {suffix}
            </span>
            <SicherheitAbzeichen sicherheit={feld.sicherheit} />
        </span>
    );
    if (!text) {
        return inhalt;
    }
    return (
        <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id={`herkunft-${eindeutigeId}`}>{text}</Tooltip>}>
            <span>{inhalt}</span>
        </OverlayTrigger>
    );
}

/**
 * Kennzahl-Kacheln am Kopf des Artikel-Blatts (PLAN.md Abschnitt 5): Bestand,
 * „offen, nicht reserviert“ (Planung), Basisverbrauch, Reichweite je Szenario, die vier
 * Marken (aus dem Szenario "erwartet"), Bestellmenge und die Warnung
 * "zu spät" mit dem genauen Wortlaut aus PLAN.md 4.5.
 */
export default function ArtikelKennzahlKacheln({prognose, offeneKundenmenge, paletteStueck}) {
    if (!prognose) {
        return null;
    }

    if (prognose.status !== "gerechnet") {
        return (
            <Card className="shadow-sm mb-3 border-secondary-subtle">
                <Card.Body className="py-2 px-3 text-muted">{prognose.grund_nicht_gerechnet}</Card.Body>
            </Card>
        );
    }

    const erwartet = prognose.szenarien.erwartet;
    const sicher = prognose.szenarien.sicher;
    const bestellt = prognose.szenarien.bestellt;
    const warnung = erwartet.warnung_zu_spaet;
    const bestellmenge = erwartet.bestellmenge;

    return (
        <div>
            <div className="d-flex flex-wrap gap-2 mb-2">
                <Kachel label="Bestand (Start)" hilfe={`${herkunftText(prognose.bestand_start) || "Lagerbestand minus „offen, nicht reserviert“."} ${COSMOS_PLANUNG_HINWEIS}`}>
                    <WertMitHerkunft feld={prognose.bestand_start} />
                </Kachel>
                <Kachel label="Offen, nicht reserviert (Planung)" hilfe={`${herkunftText(offeneKundenmenge) || "Aus Aufträgen im Erfassungsstadium, enge Definition."} ${COSMOS_PLANUNG_HINWEIS}`}>
                    <WertMitHerkunft feld={offeneKundenmenge} />
                </Kachel>
                <Kachel label="Basisverbrauch / Woche" hilfe={herkunftText(prognose.wochenbedarf_basis) || "Gestutzter Mittelwert der letzten 12 vollen Wochen."}>
                    <WertMitHerkunft feld={prognose.wochenbedarf_basis} />
                </Kachel>
                {/* Auftrag #51 (28.09.2026): Anteil der Verkäufe ab dem Lager, aus dem
                    gerechnet wird - nur sichtbar, wenn eine Aufteilung vorliegt. */}
                {prognose.lageranteil?.sicherheit === "aus_datei" && (
                    <Kachel label="Anteil ab diesem Lager" hilfe={herkunftText(prognose.lageranteil)}>
                        <WertMitHerkunft feld={prognose.lageranteil} format={(w) => (w * 100).toLocaleString("de-DE", {minimumFractionDigits: 1, maximumFractionDigits: 1})} suffix=" %" />
                    </Kachel>
                )}
                <Kachel label="Reichweite (sicher / erwartet / bestellt)" hilfe="Tage bis der Bestand im jeweiligen Szenario auf null fällt.">
                    <span className="fw-semibold">
                        {formatMenge(sicher.reichweite_tage.wert)} / {formatMenge(erwartet.reichweite_tage.wert)} / {formatMenge(bestellt.reichweite_tage.wert)} Tage
                    </span>
                </Kachel>
                <Kachel label="Leer am (T leer)" hilfe={herkunftText(erwartet.t_leer) || "Erster Tag mit Bestand kleiner/gleich null, Szenario erwartet."}>
                    <span className="fw-semibold">{erwartet.t_leer.wert ? formatDatum(erwartet.t_leer.wert) : "–"}</span>
                </Kachel>
                <Kachel label="Idealer Bestelltermin (T ideal)" hilfe={herkunftText(erwartet.t_ideal) || "Hier sollte bestellt werden."}>
                    <span className="fw-semibold">{erwartet.t_ideal?.wert ? formatDatum(erwartet.t_ideal.wert) : "–"}</span>
                </Kachel>
                <Kachel label="Letzte Gelegenheit (T spät)" hilfe={herkunftText(erwartet.t_spaet) || "Letzte Gelegenheit, noch rechtzeitig zu bestellen."}>
                    <span className="fw-semibold">{erwartet.t_spaet?.wert ? formatDatum(erwartet.t_spaet.wert) : "–"}</span>
                </Kachel>
                <Kachel label="Bestellmenge Q" hilfe={herkunftText(bestellmenge) || "Zielbestand Z minus erwarteter Bestand bei Ankunft zum Idealtermin (mindestens die negative offene Menge „offen, nicht reserviert“), aufgerundet auf volle Paletten – reicht das nicht mehr in die Lagerkapazität, wird stattdessen abgerundet."}>
                    <span className="fw-semibold">
                        {bestellmenge?.wert !== null && bestellmenge?.wert !== undefined
                            ? `${formatMenge(bestellmenge.wert)} Dosen${paletteStueck ? ` (${formatMenge(bestellmenge.wert / paletteStueck)} Pal.)` : ""}`
                            : "–"}
                    </span>
                </Kachel>
            </div>

            {warnung?.wert && (
                <div className="alert alert-danger d-flex align-items-center gap-2 py-2">
                    <PiWarningBold className="flex-shrink-0" aria-hidden="true" />
                    <span>
                        Für diesen Farbton ist das nächste Fenster zu spät, es entsteht eine Lücke von{" "}
                        {warnung.luecke_wochen !== null ? warnung.luecke_wochen : "unbekannt"} Wochen.
                    </span>
                </div>
            )}
        </div>
    );
}
